/**
 * NotificationBell — header bell + dropdown panel.
 *
 * Lives in `RootLayout` next to the avatar. Click toggles the
 * dropdown; the dropdown polls `/notifications` while open and
 * shows the last poll's data while closed (so the badge count
 * updates in the background).
 *
 * Polling cadence:
 *   * Closed: every 2 minutes (just to keep the badge fresh).
 *   * Open:   every 30 seconds (so new mentions land mid-session).
 *
 * There's no websocket layer yet. If the deployment ever needs
 * lower latency we'll swap to SSE — the API contract is already
 * shaped for it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  AtSign,
  Bell,
  Check,
  GitBranch,
  GitMerge,
  Lightbulb,
  Loader2,
  MessageSquareWarning,
  Sparkles,
  TrendingUp,
  X,
} from "lucide-react";
import { useNavigate } from "react-router";

import {
  fetchNotifications,
  markAllRead,
  markRead,
  type Notification,
  type NotificationKind,
  type NotificationTab,
} from "../../../lib/notifications";

const POLL_OPEN_MS = 30_000;
const POLL_CLOSED_MS = 120_000;

const TABS: { id: NotificationTab; label: string }[] = [
  { id: "all", label: "All" },
  { id: "mention", label: "Mentions" },
  { id: "activity", label: "Activity" },
  { id: "update", label: "Updates" },
];

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<NotificationTab>("all");
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async (selectedTab: NotificationTab) => {
    setLoading(true);
    try {
      const list = await fetchNotifications(selectedTab);
      setItems(list.items);
      setUnread(list.unread_count);
    } catch {
      // Silent — bell never blocks the user.
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + polling. Cadence shifts when the dropdown opens
  // so we don't spam the API while it's closed.
  useEffect(() => {
    void refresh(tab);
    const interval = open ? POLL_OPEN_MS : POLL_CLOSED_MS;
    const id = window.setInterval(() => {
      void refresh(tab);
    }, interval);
    return () => window.clearInterval(id);
  }, [open, tab, refresh]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  async function onMarkAllRead() {
    await markAllRead();
    void refresh(tab);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        className="relative flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-900"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            aria-label={`${unread} unread`}
            className="absolute -right-0.5 -top-0.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-30 w-[380px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          <Header
            unread={unread}
            loading={loading}
            onMarkAllRead={onMarkAllRead}
            onClose={() => setOpen(false)}
          />
          <Tabs current={tab} onPick={setTab} />
          <List
            items={items}
            loading={loading}
            onSelect={() => setOpen(false)}
            onMarkRead={async (id) => {
              await markRead(id);
              void refresh(tab);
            }}
          />
        </div>
      )}
    </div>
  );
}

function Header({
  unread,
  loading,
  onMarkAllRead,
  onClose,
}: {
  unread: number;
  loading: boolean;
  onMarkAllRead: () => void;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 px-3.5 py-2.5">
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-medium text-slate-900">
          Notifications
        </span>
        {loading && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onMarkAllRead}
          disabled={unread === 0}
          className="rounded px-2 py-1 text-[11.5px] text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40"
        >
          Mark all read
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-6 w-6 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function Tabs({
  current,
  onPick,
}: {
  current: NotificationTab;
  onPick: (t: NotificationTab) => void;
}) {
  return (
    <div className="flex items-center gap-3 border-b border-slate-100 px-3.5">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onPick(t.id)}
          className={[
            "py-2 text-[12px] transition-colors",
            current === t.id
              ? "text-slate-900 font-medium"
              : "text-slate-500 hover:text-slate-900",
          ].join(" ")}
          style={
            current === t.id
              ? {
                  boxShadow: "inset 0 -2px 0 0 var(--accent, #534AB7)",
                  color: "var(--accent, #3C3489)",
                }
              : undefined
          }
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function List({
  items,
  loading,
  onSelect,
  onMarkRead,
}: {
  items: Notification[];
  loading: boolean;
  onSelect: () => void;
  onMarkRead: (id: string) => Promise<void>;
}) {
  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-[12px] text-slate-400">
        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-[12.5px] text-slate-500">
        Nothing here yet — you&apos;ll see mentions, MR activity and
        product updates land here.
      </div>
    );
  }
  return (
    <div className="max-h-[440px] overflow-y-auto">
      {items.map((n) => (
        <NotificationRow
          key={n.id}
          notification={n}
          onSelect={onSelect}
          onMarkRead={onMarkRead}
        />
      ))}
    </div>
  );
}

function NotificationRow({
  notification,
  onSelect,
  onMarkRead,
}: {
  notification: Notification;
  onSelect: () => void;
  onMarkRead: (id: string) => Promise<void>;
}) {
  const navigate = useNavigate();
  const isUnread = !notification.read_at;

  async function open() {
    if (isUnread) {
      // Fire-and-forget — UX-wise we don't want to block the
      // navigation on the network round-trip.
      void onMarkRead(notification.id);
    }
    const target = deepLinkFor(notification);
    if (target) navigate(target);
    onSelect();
  }

  return (
    <button
      type="button"
      onClick={open}
      className={[
        "flex w-full items-start gap-2.5 border-b border-slate-50 px-3.5 py-2.5 text-left transition-colors hover:bg-slate-50",
        isUnread ? "bg-violet-50/40" : "",
      ].join(" ")}
    >
      <Avatar notification={notification} />
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] leading-snug text-slate-900">
          {notification.title}
        </p>
        {notification.body && (
          <p className="mt-0.5 line-clamp-2 text-[11.5px] leading-snug text-slate-500">
            {notification.body}
          </p>
        )}
        <p className="mt-1 text-[10.5px] text-slate-400">
          {formatRelative(notification.created_at)} ·{" "}
          {kindLabel(notification.kind)}
        </p>
      </div>
      {isUnread && (
        <span
          aria-hidden
          className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-rose-500"
        />
      )}
    </button>
  );
}

function Avatar({ notification }: { notification: Notification }) {
  // Two visual variants: actor-led (mentions, comment replies →
  // initials), system-led (MR / news → kind-specific icon). Same
  // 28×28 footprint so rows align.
  if (notification.actor_initials) {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-700 text-[10px] font-medium text-white">
        {notification.actor_initials}
      </span>
    );
  }
  // lucide-react icons are React components (forwardRef objects), not
  // plain callables — they have to be rendered as JSX. Capitalised
  // local alias so JSX treats `<KindIcon />` as a component.
  const KindIcon = ICON_FOR_KIND[notification.kind] ?? Bell;
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
      <KindIcon className="h-3.5 w-3.5" />
    </span>
  );
}

const ICON_FOR_KIND: Record<NotificationKind, typeof Bell> = {
  mention: AtSign,
  comment_reply: AtSign,
  mr_opened: GitBranch,
  mr_approved: Check,
  mr_merged: GitMerge,
  mr_rejected: X,
  branch_forked: GitBranch,
  product_update: Sparkles,
  tip: Lightbulb,
  forecast_alert: TrendingUp,
  feedback_alert: MessageSquareWarning,
};

function kindLabel(kind: NotificationKind): string {
  switch (kind) {
    case "mention":
      return "Mention";
    case "comment_reply":
      return "Reply";
    case "mr_opened":
      return "Change request";
    case "mr_approved":
      return "Approved";
    case "mr_merged":
      return "Merged";
    case "mr_rejected":
      return "Rejected";
    case "branch_forked":
      return "Branch";
    case "product_update":
      return "What's new";
    case "tip":
      return "Tip";
    case "forecast_alert":
      return "Forecast alert";
    case "feedback_alert":
      return "Product feedback";
    default:
      return "Activity";
  }
}

function deepLinkFor(n: Notification): string | null {
  if (!n.source_type) return null;
  // Append the comment's id as a URL hash so the destination
  // page's CommentsThread can scroll to + highlight the exact
  // comment that triggered the notification, not just land on
  // the surface and leave the user hunting.
  const hash = n.comment_id ? `#comment-${n.comment_id}` : "";
  switch (n.source_type) {
    case "branch":
      return n.source_id ? `/branches/${n.source_id}/work${hash}` : null;
    case "merge_request":
      return n.source_id ? `/merge-requests/${n.source_id}${hash}` : null;
    case "pinned_card":
      return `/${hash}`;
    case "dataset":
      return `/data${hash}`;
    case "forecast":
      // Land on the forecast with the breaching series prefilled + highlighted.
      return n.source_id ? `/forecast?alert=${n.source_id}` : "/alerts";
    case "product_news":
      return null;
    case "product_feedback":
      return n.kind === "feedback_alert"
        ? (n.source_id ? `/platform?section=feedback&feedback=${n.source_id}` : "/platform?section=feedback")
        : (n.source_id ? `/home?feedback=${n.source_id}` : "/home");
    default:
      return null;
  }
}

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const min = Math.round(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(t).toLocaleDateString();
}
